// Generic confirm-then-delete state machine for list screens. Per
// spec/build-plan.md P-024 row + the deduplication path surfaced
// during the P-024 /simplify review.
//
// **Why a hook.** Both the Template list and Email list screens share
// identical confirm-modal machinery: a `pendingDeleteId: string | null`
// state, a `pending = items.find(i => i.id === pendingId)` lookup, and
// the same on-confirm / on-cancel handlers that close the modal. Two
// uses today; the editor screens at P-031 / P-032 may grow this to a
// third or fourth instance. Co-locating in a hook removes the
// duplication and keeps the modal contract in one place.
//
// **Open is derived from `pending !== undefined`.** No separate open
// state — the lookup is the single source of truth. If the caller
// removes the item between request and confirm (rare race), `pending`
// becomes `undefined` and the modal closes automatically.

import { useState } from 'preact/hooks'

interface UseDeleteConfirmResult<T> {
  /** The item targeted for deletion, or `undefined` when the modal is closed. */
  readonly pending: T | undefined
  /** Open the confirm modal targeting `id`. */
  readonly request: (id: string) => void
  /** Run the on-delete callback for the pending item, then close the modal. */
  readonly onConfirm: () => void
  /** Close the modal without deleting. */
  readonly onCancel: () => void
}

export function useDeleteConfirm<T extends { id: string }>(
  items: readonly T[],
  onDelete: (id: string) => void,
): UseDeleteConfirmResult<T> {
  const [pendingId, setPendingId] = useState<string | null>(null)
  const pending = pendingId === null ? undefined : items.find((x) => x.id === pendingId)
  return {
    pending,
    request: (id) => setPendingId(id),
    onConfirm: () => {
      if (pendingId !== null) onDelete(pendingId)
      setPendingId(null)
    },
    onCancel: () => setPendingId(null),
  }
}
